import { motion } from 'framer-motion';
import { CheckCircle2, Quote } from 'lucide-react';
import { Reveal, Overline, SectionHeader } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { CASE_STUDIES } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const CaseHero = () => (
    <section className="relative overflow-hidden pt-44 pb-20 sm:pb-24" data-testid="case-studies-hero">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                <Overline>Case Studies</Overline>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                {['Readiness, proven', 'in the field.'].map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                        >
                            {i === 1 ? <>in the <span className="text-deepteal">field.</span></> : line}
                        </motion.span>
                    </span>
                ))}
            </h1>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
                A selection of engagements across healthcare, financial services, technology, and logistics.
                Details are anonymized and blended to protect client confidentiality; the outcomes are real.
            </motion.p>
        </div>
    </section>
);

const CaseBlock = ({ study, index }) => (
    <Reveal delay={0.05}>
        <article className="border-t border-slate-800 py-16 first:border-t-0" data-testid={`case-study-${study.id}`}>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-sm text-slate-500">{study.num}</span>
                        <span className="rounded-full border border-deepteal/20 bg-deepteal/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-deepteal">
                            {study.industry}
                        </span>
                    </div>
                    <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 text-balance">
                        {study.title}
                    </h2>
                    <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{study.client}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {study.metrics.map((m) => (
                            <div key={m.label} className="rounded-xl border border-slate-700 bg-navy-800/60 px-4 py-3">
                                <p className="font-display text-xl font-extrabold text-deepteal">{m.value}</p>
                                <p className="mt-1 max-w-[9rem] text-[11px] leading-snug text-slate-500">{m.label}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-8">
                    <div className="flex gap-4">
                        <Quote size={18} className="mt-1 shrink-0 text-deepteal/60" />
                        <p className="text-base sm:text-lg leading-relaxed text-slate-300">{study.situation}</p>
                    </div>

                    <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">What we did</h3>
                            <ul className="mt-4 space-y-3">
                                {study.approach.map((a) => (
                                    <li key={a} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-glow/60" />
                                        {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">What changed</h3>
                            <ul className="mt-4 space-y-3">
                                {study.outcomes.map((o) => (
                                    <li key={o} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                                        <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-deepteal" />
                                        {o}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    </Reveal>
);

export default function CaseStudies() {
    useSEO(
        'Case Studies — Resilience Engagements in the Field | Planmodo',
        'Anonymized resilience and recovery engagements: ransomware exercises in healthcare, DORA readiness in financial services, incident operations in SaaS, and network resilience in logistics.'
    );
    return (
        <>
            <CaseHero />
            <section className="pb-20 sm:pb-28" data-testid="case-studies-list">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    {CASE_STUDIES.map((study, i) => (
                        <CaseBlock key={study.id} study={study} index={i} />
                    ))}
                    <Reveal delay={0.1}>
                        <p className="mt-4 border-t border-slate-800 pt-10 text-xs leading-relaxed text-slate-500" data-testid="case-studies-disclaimer">
                            Client details are anonymized and, in places, blended across comparable engagements to protect
                            confidentiality. Scenarios, scope, and outcomes reflect real Planmodo delivery work.
                        </p>
                    </Reveal>
                </div>
            </section>
            <CTASection
                title="Your organization could be the next story."
                text="Bring us your toughest disruption scenario. We will show you exactly how a tested program would handle it — and build the capability if it is not there yet."
            />
        </>
    );
}
